import { CONFIG } from '../config/constants.js';

/**
 * Applies morale modifier to a dice roll
 * Morale affects worker efficiency:
 * - 75-100: +1 to all rolls
 * - 50-74: Normal (no change)
 * - 25-49: -1 to all rolls
 * - 0-24: -2 to all rolls
 *
 * @param {number} roll - Base dice roll (after injury penalty)
 * @param {number} morale - Current morale (0-100)
 * @returns {number} Modified roll (minimum 1)
 */
export function applyMoraleModifier(roll, morale) {
  if (morale >= 75) return roll + 1;
  if (morale >= 50) return roll;
  if (morale >= 25) return Math.max(1, roll - 1);
  return Math.max(1, roll - 2);
}

/**
 * Calculates morale change based on events
 * @param {number} currentMorale - Current morale value
 * @param {Object} events - Events that affect morale
 * @param {boolean} events.combatWon - Won a raid
 * @param {boolean} events.combatLost - Lost a raid
 * @param {number} events.deaths - Number of deaths this day
 * @param {boolean} events.starvation - Starvation occurred
 * @param {boolean} events.buildingComplete - Building was completed
 * @param {number} events.eventMorale - Morale change from event (e.g., storm -3)
 * @param {boolean} events.birth - Birth occurred
 * @returns {number} New morale value (clamped 0-100)
 */
export function calculateMoraleChange(currentMorale, events = {}) {
  let morale = currentMorale;

  // Combat results
  if (events.combatWon) morale += 10;
  if (events.combatLost) morale -= 20;

  // Deaths
  if (events.deaths > 0) morale -= events.deaths * 10;

  // Starvation
  if (events.starvation) morale -= 5;

  // Positive events
  if (events.buildingComplete) morale += 5;
  if (events.birth) morale += 10;

  // Event-specific morale changes
  if (events.eventMorale !== undefined) morale += events.eventMorale;

  // Clamp between 0 and 100
  return Math.max(0, Math.min(100, morale));
}

/**
 * Checks if a worker defects due to low morale
 * @param {number} morale - Current morale
 * @param {Array} workers - Array of workers
 * @returns {Object|null} Defecting worker or null
 */
export function checkDefection(morale, workers) {
  if (morale >= CONFIG.MORALE_DEFECTION_THRESHOLD) return null;

  // Calculate defection chance based on morale
  const defectionChance = morale < 25 ? 0.15 : 0.05; // 15% or 5%

  // Roll for defection
  const roll = Math.random();
  if (roll > defectionChance) return null;

  // Find a healthy worker to defect (don't let injured/immature/dead defect)
  const healthyWorkers = workers.filter(w =>
    w.status === 'healthy' &&
    w.id !== 'temp' // Temp workers can't defect
  );

  if (healthyWorkers.length === 0) return null;

  // Prevent defection if only 1-2 workers remain
  const aliveCount = workers.filter(w => w.status !== 'dead').length;
  if (aliveCount <= 2) return null;

  // Random healthy worker defects
  const randomIndex = Math.floor(Math.random() * healthyWorkers.length);
  return healthyWorkers[randomIndex];
}

/**
 * Gets morale effect description for UI
 * @param {number} morale - Current morale
 * @returns {Object} { modifier: string, defectionRisk: string }
 */
export function getMoraleEffect(morale) {
  if (morale >= 75) {
    return { modifier: '+1 to all rolls', defectionRisk: 'None' };
  }
  if (morale >= 50) {
    return { modifier: 'Normal performance', defectionRisk: 'None' };
  }
  if (morale >= 25) {
    return { modifier: '-1 to all rolls', defectionRisk: '5% per day' };
  }
  return { modifier: '-2 to all rolls', defectionRisk: '15% per day' };
}
